import {diff} from 'deep-object-diff';
import produce from 'immer';
import _ from 'lodash';

export const applyDiff = (
  originalObject,
  updatedObject,
  objectToApplyDiff,
  diffResults
) => {
  if (
    (!objectToApplyDiff ||
      (_.isArray(objectToApplyDiff) && _.isEmpty(objectToApplyDiff))) &&
    Array.isArray(diffResults)
  ) {
    //console.log('returning diffResults as is', diffResults);
    return diffResults;
  }
  //if (Array.isArray(diffResults)) return diffResults;
  return produce(objectToApplyDiff, (draft) => {
    _.forEach(diffResults, (value, key) => {
      //called twice for arrays in draft at key after fixing indexes in value
      draft = applyDiffResultEntry(
        key,
        value,
        draft,
        originalObject,
        updatedObject,
        false
      );
    });
    //An immer producer returned a new value *and* modified its draft. Either return a new value *or* modify the draft.
    if (Array.isArray(draft)) {
      for (let i = draft.length - 1; i >= 0; i--) {
        if (draft[i] === null) {
          //console.log('splicing null values from final draft', draft.length);
          draft.splice(i, 1);
        }
      }
    }
    return draft;
  });
};

const applyDiffResultEntry = (
  key,
  value,
  draft,
  originalObject,
  updatedObject,
  diffArrayCompleted
) => {
  if (value === undefined) {
    if (Array.isArray(draft) && draft.length > 0 && key < draft.length) {
      //console.log('setting draft[key] to null', key);
      draft[key] = null;
    } else {
      //console.log('deleting draft[key]: key, draft[key]', key, draft[key]);
      delete draft[key];
    }
  } else if (Array.isArray(value)) {
    // deep-object-diff only returns an array
    // when we are adding the whole thing
    if (draft && Array.isArray(draft[key])) {
      //if local doesn't have object with same id as in remote then add those to local otherwise replace the object in local
      draft[key] = draft[key] || [];
      _.forEach(value, (obj) => {
        const index = _.findIndex(
          draft[key],
          (item) =>
            item.id !== null &&
            item.id !== undefined &&
            String(item.id) === String(obj.id)
        );
        if (index !== -1) {
          //console.log('draft[key][index], obj', draft[key][index], obj);
          draft[key][index] = obj;
        } else {
          //console.log('draft[key], obj', draft[key], obj);
          draft[key] = _.concat(draft[key], obj);
        }
      });
    } else {
      draft = draft || {};
      //console.log('draft[key], value', draft[key], value);
      draft[key] = value;
    }
  } else if (typeof value === 'object') {
    //if there are no diffs to be applied at the key then do nothing.
    if (!_.isEmpty(value)) {
      draft = draft || {};
      //If array diff is being applied then fix indexes for updates and adds. And handle all deletes before calling recursively.
      //If draft[key] (state) is undefined we wouldn't know if this value object is related to array diff. So checking type in originalObject and updatedObject as well.
      if (
        !diffArrayCompleted &&
        isObjectTypeAnArrayWithIds(
          draft[key],
          originalObject?.[key],
          updatedObject?.[key]
        )
      ) {
        //draft[key] at this point could still be null, undefind, [] or array of objects
        const {
          indexedDiffResults: indexedDiffResults,
          arrayToApplyDiff: arrayToApplyDiffModified,
        } = diffArrays(draft[key], originalObject[key], updatedObject[key]);
        //indexedDiffResults could be an array or object
        value = indexedDiffResults;
        //console.log('key, draft[key], arrayToApplyDiffModified', key, draft[key]?.length, arrayToApplyDiffModified?.length);
        draft[key] = arrayToApplyDiffModified || [];
        //after fixing array index in diff result and deleting undefined entries from state call applyDiffResultEntry again to apply diff on correct index in array.
        draft = applyDiffResultEntry(
          key,
          value,
          draft,
          originalObject,
          updatedObject,
          true
        );
      } else {
        draft[key] = applyDiff(
          originalObject?.[key],
          updatedObject?.[key],
          draft[key],
          value
        );
      }
    }
  } else {
    draft = draft || {};
    //console.log('draft set literal draft[key], key, value',draft[key], key, value);
    draft = _.set(draft, key, value);
  }
  return draft;
};

const isObjectTypeAnArrayWithIds = (stateObject, originalObject, updatedObject) => {
  if (
    (stateObject && allObjectsHaveId(stateObject)) ||
    (updatedObject && allObjectsHaveId(updatedObject)) ||
    (originalObject && allObjectsHaveId(originalObject))
  ) {
    return true;
  }
};

const allObjectsHaveId = (array) => {
  if (!Array.isArray(array)) {
    return false;
  }
  return (
    array.length > 0 &&
    array.every((item) => {
      return item && typeof item === 'object' && 'id' in item;
    })
  );
};

export const produceAppliedDiff = (
  originalObjectRaw,
  updatedObjectRaw,
  objectToApplyDiffRaw
) => {
  //if originalObject has array entry undefined then diff will produce array as is from updatedObject in diffResults.
  //otherwise if originalObject has array object but none of elements match/there are updates in updatedObject corresponding array then diff will produce an object(map) version
  //of the array from updatedObject in diffResults. With the keys of the object in diffResults as index of elements in the array that were compared.
  let originalObject = {
    rootKey: originalObjectRaw,
  };
  let updatedObject = {
    rootKey: updatedObjectRaw,
  };
  let objectToApplyDiff = {
    rootKey: objectToApplyDiffRaw,
  };

  const diffResults = diff(originalObject, updatedObject);
  //console.log('diffResults: ', diffResults);
  let resultWithDiffApplied = applyDiff(
    originalObject,
    updatedObject,
    objectToApplyDiff,
    diffResults
  );
  //console.log('resultWithDiffApplied', JSON.stringify(_.get(resultWithDiffApplied, 'rootKey'), null, 2));
  return _.get(resultWithDiffApplied, 'rootKey');
  //console.log('resultWithDiffApplied', JSON.stringify(resultWithDiffApplied, null, 2));
  //return resultWithDiffApplied;
};

const diffArrays = (arrayToApplyDiff, originalArray, updatedArray) => {
  const originalArrayLookupMap = _.keyBy(originalArray, 'id');
  const diffResultsById = {};
  //1. identify modified or added objects
  _.forEach(updatedArray, (updArrayObj) => {
    const prevArrayObj = originalArrayLookupMap[updArrayObj.id];
    if (prevArrayObj) {
      const diffObj = diff(prevArrayObj, updArrayObj);
      diffResultsById[updArrayObj.id] = diffObj;
    } else {
      diffResultsById[updArrayObj.id] = updArrayObj;
    }
  });
  //2. identify deleted objects and delete them from corresponding state array in objectToApplyDiff
  const deletedIds = _.difference(
    Object.keys(originalArrayLookupMap),
    Object.keys(diffResultsById)
  );
  if (arrayToApplyDiff) {
    _.forEach(deletedIds, (id) => {
      const indexToRemove = arrayToApplyDiff.findIndex(
        (obj) => String(obj.id) === String(id)
      );
      if (indexToRemove !== -1) {
        //console.log('draft deletions', indexToRemove, draft.length);
        arrayToApplyDiff.splice(indexToRemove, 1);
      }
    });
  }

  //3. set keys in diff results based on objectToApplyDiff array indexes
  let indexedDiffResults;
  const targetArray = arrayToApplyDiff || [];
  let addIndex = targetArray.length;
  if (addIndex > 0) {
    indexedDiffResults = {};
  } else {
    indexedDiffResults = []; //all diffs are going to be added as new array to state
  }
  _.forEach(diffResultsById, (value, key) => {
    // Skip undefined values and empty objects
    if (value === undefined || (_.isObject(value) && _.isEmpty(value))) {
      return;
    }
    if (targetArray.length > 0) {
      const i = _.findIndex(
        targetArray,
        (item) =>
          item.id !== null && item.id !== undefined && String(item.id) === String(key)
      );
      if (i !== -1) {
        //modified remotely and object present in state
        indexedDiffResults[i] = value;
      } else if ('id' in value) {
        //added remotely but object not present in state
        indexedDiffResults[addIndex++] = value;
      } else {
        //modified remotely but not present in state
      }
    } else if ('id' in value) {
      //added remotely but whole array not present in state
      indexedDiffResults.push(value);
    }
    {
      //modified remotely but not present in state
    }
  });

  //console.log('indexedDiffResults', indexedDiffResults);
  return {indexedDiffResults, arrayToApplyDiff};
};

/**
 * Recursively searches through an object and all its nested structures
 * to find if an id attribute with the specified value exists
 */
export const hasIdWithValue = (obj, idValue) => {
  if (obj === null || obj === undefined) {
    return false;
  }
  if (typeof obj !== 'object') {
    return false;
  }
  if (obj.id !== undefined && String(obj.id) === String(idValue)) {
    return true;
  }
  for (const key in obj) {
    if (hasIdWithValue(obj[key], idValue)) {
      return true;
    }
  }
  return false;
};
